// ui/src/components/finance/PeriodCloseWorkflow.jsx
/**
 * @file PeriodCloseWorkflow.jsx
 * @module ui/src/components/finance/PeriodCloseWorkflow
 * @description
 *   Renders the server-authoritative fiscal-period close gate workflow and its
 *   unresolved/resolved exceptions. Close workflow state remains permanently separate
 *   from the immutable close-package artifact rendered by ClosePackagePanel.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #25
 *   Temporal invariants : INV-9
 *   Design principles : DP-10, DP-14
 *   Cross-cutting contracts : XC-5, XC-7
 *
 * @domain-rules
 *   - Gates render in the prescribed close sequence while each gate's label/status
 *     remains server supplied.
 *   - gate.canAdvance is authoritative; the browser never derives advance eligibility.
 *   - Gate mutations are never optimistic.
 *   - Resolved exceptions remain visible for audit context.
 *   - Close workflow state and generated package existence are independent concepts.
 *
 * @session-6-migration-flags
 *   - GET /api/v1/finance/period/{id}/close-status — Session 6 API gap
 *   - POST /api/v1/finance/period/{id}/close-status/advance — Session 6 API gap
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import ClosePackagePanel from './ClosePackagePanel';

/**
 * @typedef {Object} CloseGate
 * @property {string} gateKey
 * @property {string} gateLabel
 * @property {'not_started'|'in_progress'|'passed'|'blocked'} gateStatus
 * @property {boolean} canAdvance
 * @property {string|null} blockedReason
 * @property {string|null} completedAt
 * @property {string|null} completedBy
 */

/**
 * @typedef {Object} CloseException
 * @property {string} exceptionId
 * @property {string} exceptionType
 * @property {string} description
 * @property {'blocking'|'warning'} severity
 * @property {string|null} resolvedAt
 * @property {string|null} resolvedBy
 */

/**
 * @typedef {Object} PeriodCloseWorkflowProps
 * @property {number} fiscalPeriodId
 * @property {Object} currentUser
 */

const GATE_SEQUENCE = [
  'data_readiness',
  'calculation_complete',
  'exceptions_resolved',
  'settlements_reconciled',
  'payroll_gl_reconciled',
  'period_locked',
];

const GATE_FALLBACK_LABELS = {
  data_readiness: 'Data readiness',
  calculation_complete: 'Calculation complete',
  exceptions_resolved: 'Exceptions resolved',
  settlements_reconciled: 'Settlements reconciled',
  payroll_gl_reconciled: 'Payroll / GL reconciliation',
  period_locked: 'Period locked',
};

const GATE_BADGE_CLASSES = {
  not_started: 'modifier-manager-badge--not-created',
  in_progress: 'modifier-manager-badge--incomplete',
  passed: 'modifier-manager-badge--complete',
  blocked: 'modifier-manager-badge--incomplete',
};

const styles = {
  gateList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  gate: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '0.5rem',
    padding: '0.75rem',
  },
  gateHeader: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    justifyContent: 'space-between',
  },
  exceptionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  meta: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.8125rem',
  },
};

function formatDateTime(value) {
  if (!value) return 'Not available';
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function ExceptionGroup({ exceptions, className, title }) {
  // WHY: Empty severity groups are omitted to keep Finance focused while resolved
  // records inside a non-empty group remain visible.
  if (!exceptions.length) return null;

  return (
    <div className={className}>
      <strong>{title}</strong>
      <div style={styles.exceptionList}>
        {exceptions.map((exception) => (
          <div key={exception.exceptionId}>
            <div>
              <strong>{exception.exceptionType}</strong>
              {' — '}
              {exception.description}
            </div>
            {/*
             * WHY: Resolved exceptions remain visible with audit timestamp and actor.
             */}
            {exception.resolvedAt ? (
              <div style={styles.meta}>
                Resolved {formatDateTime(exception.resolvedAt)}
                {exception.resolvedBy
                  ? ` · ${exception.resolvedBy}`
                  : ''}
              </div>
            ) : (
              <div style={styles.meta}>Unresolved</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

ExceptionGroup.propTypes = {
  className: PropTypes.string.isRequired,
  exceptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
};

function PeriodCloseWorkflow({
  fiscalPeriodId,
  currentUser,
}) {
  // WHY: closeStatus is the complete server-authoritative workflow snapshot.
  const [closeStatus, setCloseStatus] = useState(null);

  // WHY: Exceptions are retained independently for ordered severity presentation.
  const [exceptions, setExceptions] = useState([]);

  // WHY: Loading state distinguishes workflow retrieval from not-started gate state.
  const [loading, setLoading] = useState(false);

  // WHY: Global status-fetch failures must not be mistaken for blocked gates.
  const [error, setError] = useState('');

  // WHY: Only the gate currently undergoing a server mutation is marked advancing.
  const [advancingGate, setAdvancingGate] = useState(null);

  // WHY: Per-gate mutation errors stay attached to the gate whose authoritative
  // advancement failed rather than contaminating all workflow state.
  const [gateErrors, setGateErrors] = useState({});

  const loadCloseStatus = async (signal) => {
    setLoading(true);
    setError('');

    try {
      // WHY: Workflow state, canAdvance, allGatesPassed and package identity must
      // all come from the server close-status endpoint.
      const response = await fetch(
        `/api/v1/finance/period/${fiscalPeriodId}/close-status`,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal,
        },
      );

      if (!response.ok) {
        throw new Error(`Period close status failed (${response.status}).`);
      }

      const payload = await response.json();
      setCloseStatus(payload);
      setExceptions(Array.isArray(payload.exceptions) ? payload.exceptions : []);
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setError(
          requestError.message || 'Period close workflow could not be loaded.',
        );
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    // WHY: Each fiscal period has a distinct close state machine.
    loadCloseStatus(controller.signal);

    // WHY: Prevent an obsolete period response from replacing a newer selection.
    return () => controller.abort();
  }, [fiscalPeriodId]);

  const orderedGates = useMemo(() => {
    // WHY: The contractual gate sequence is fixed. Unknown future gates are appended
    // rather than silently discarded, while known gates remain in the required order.
    if (!closeStatus?.gates) return [];

    const byKey = new Map(
      closeStatus.gates.map((gate) => [gate.gateKey, gate]),
    );

    const known = GATE_SEQUENCE
      .map((gateKey) => byKey.get(gateKey))
      .filter(Boolean);

    const unknown = closeStatus.gates.filter(
      (gate) => !GATE_SEQUENCE.includes(gate.gateKey),
    );

    return [...known, ...unknown];
  }, [closeStatus]);

  const blockingExceptions = useMemo(
    () =>
      exceptions.filter(
        (exception) => exception.severity === 'blocking',
      ),
    [exceptions],
  );

  const warningExceptions = useMemo(
    () =>
      exceptions.filter(
        (exception) => exception.severity === 'warning',
      ),
    [exceptions],
  );

  const handleAdvanceGate = async (gate) => {
    // WHY: canAdvance is server-determined eligibility and must be obeyed directly.
    if (!gate.canAdvance || gate.gateStatus === 'passed') {
      return;
    }

    setAdvancingGate(gate.gateKey);
    setGateErrors((current) => ({
      ...current,
      [gate.gateKey]: '',
    }));

    try {
      // WHY: Advancing a close gate is a governance mutation; no local gate status
      // changes until this endpoint returns the authoritative updated gate list.
      const response = await fetch(
        `/api/v1/finance/period/${fiscalPeriodId}/close-status/advance`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ gateKey: gate.gateKey }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Gate "${gate.gateLabel}" could not be advanced (${response.status}).`,
        );
      }

      const payload = await response.json();

      // WHY: Only server-returned workflow values replace current gate state.
      setCloseStatus((current) => ({
        ...current,
        gates: payload.updatedGates,
        allGatesPassed: payload.allGatesPassed,
      }));
    } catch (requestError) {
      setGateErrors((current) => ({
        ...current,
        [gate.gateKey]:
          requestError.message || 'Gate could not be advanced.',
      }));
    } finally {
      setAdvancingGate(null);
    }
  };

  const handlePackageGenerated = () => {
    // WHY: Package generation may change existingPackageId but must not locally alter
    // any gate state; refetching lets the server return the independent artifact link.
    const controller = new AbortController();
    loadCloseStatus(controller.signal);
  };

  // WHY: Loading is not a workflow state.
  if (loading && !closeStatus) {
    return (
      <div aria-live="polite" className="modifier-manager-card" role="status">
        <div className="modifier-manager-body">
          <div className="skeleton">Loading period close workflow…</div>
        </div>
      </div>
    );
  }

  // WHY: Workflow retrieval failure is not equivalent to a blocked close gate.
  if (error && !closeStatus) {
    return (
      <div className="modifier-manager-error-state" role="alert">
        {error}
      </div>
    );
  }

  if (!closeStatus) {
    return (
      <div className="modifier-manager-empty-state" role="status">
        Close workflow state is not available.
      </div>
    );
  }

  return (
    <section
      aria-labelledby="period-close-title"
      className="modifier-manager-card"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif' }}
    >
      <div className="modifier-manager-header">
        <div>
          <h2 id="period-close-title">
            Period close · {closeStatus.periodLabel}
          </h2>
          <p>
            Period status:{' '}
            <strong>{closeStatus.periodStatus}</strong>
          </p>
        </div>
      </div>

      <div className="modifier-manager-body">
        <div className="modifier-manager-info" role="note">
          <strong>Close-state meaning.</strong>{' '}
          Closed means the period is administratively closed. Locked means no
          further calculation runs are permitted. Neither state means that a
          close package exists.
        </div>

        <ExceptionGroup
          className="modifier-manager-warning"
          exceptions={blockingExceptions}
          title="Blocking exceptions"
        />

        <div aria-label="Period close gates" style={styles.gateList}>
          {orderedGates.map((gate) => (
            <article key={gate.gateKey} style={styles.gate}>
              <div style={styles.gateHeader}>
                <div>
                  <strong>
                    {gate.gateLabel
                    || GATE_FALLBACK_LABELS[gate.gateKey]
                    || gate.gateKey}
                  </strong>
                  <div>
                    <span
                      className={`modifier-manager-badge ${
                        GATE_BADGE_CLASSES[gate.gateStatus]
                        || 'modifier-manager-badge--readonly'
                      }`}
                    >
                      {gate.gateStatus}
                    </span>
                  </div>
                </div>

                {/*
                 * WHY: The Mark complete action is rendered only from the gate's
                 * server-provided canAdvance flag and current recorded status.
                 */}
                {gate.canAdvance && gate.gateStatus !== 'passed' ? (
                  <button
                    className="secondary-button"
                    disabled={advancingGate === gate.gateKey}
                    onClick={() => handleAdvanceGate(gate)}
                    type="button"
                  >
                    {advancingGate === gate.gateKey
                      ? 'Marking complete…'
                      : 'Mark complete'}
                  </button>
                ) : null}
              </div>

              {/*
               * WHY: Passed-gate audit metadata remains visible when supplied.
               */}
              {gate.gateStatus === 'passed' ? (
                <div style={styles.meta}>
                  Completed:{' '}
                  {formatDateTime(gate.completedAt)}
                  {gate.completedBy
                    ? ` · ${gate.completedBy}`
                    : ''}
                </div>
              ) : null}

              {/*
               * WHY: Server blocking rationale tells Finance what must be resolved.
               */}
              {gate.gateStatus === 'blocked' && gate.blockedReason ? (
                <div className="modifier-manager-warning">
                  {gate.blockedReason}
                </div>
              ) : null}

              {/*
               * WHY: Mutation failure remains attached to the attempted gate.
               */}
              {gateErrors[gate.gateKey] ? (
                <div
                  className="modifier-manager-inline-error"
                  role="alert"
                >
                  {gateErrors[gate.gateKey]}
                </div>
              ) : null}
            </article>
          ))}
        </div>

        <ExceptionGroup
          className="modifier-manager-info"
          exceptions={warningExceptions}
          title="Warning exceptions"
        />

        {/*
         * WHY: C-56 is exposed only when the server says all close gates passed,
         * but package existence remains independently represented by existingPackageId.
         */}
        {closeStatus.allGatesPassed ? (
          <ClosePackagePanel
            closeStatus={{
              allGatesPassed: closeStatus.allGatesPassed,
              existingPackageId: closeStatus.existingPackageId,
              failedGates: orderedGates
                .filter((gate) => gate.gateStatus !== 'passed')
                .map((gate) => gate.gateKey),
            }}
            fiscalPeriodId={fiscalPeriodId}
            onGenerate={handlePackageGenerated}
          />
        ) : null}

        <div className="modifier-manager-readonly-note">
          Current Finance actor:{' '}
          {currentUser?.name
          || currentUser?.email
          || currentUser?.user_id
          || currentUser?.userId
          || 'Authenticated user'}
        </div>
      </div>
    </section>
  );
}

PeriodCloseWorkflow.propTypes = {
  currentUser: PropTypes.object.isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
};

/**
 * @example
 * <PeriodCloseWorkflow
 *   fiscalPeriodId={202608}
 *   currentUser={{ userId: 'finance-user-uuid', name: 'Finance Admin' }}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ Required sequence:
 *   Known gates render in data readiness → calculation → exceptions →
 *   settlements → payroll/GL → period lock order.
 *
 * ✓ Server authority:
 *   gate.canAdvance and allGatesPassed are never recomputed for advancement.
 *
 * ✓ DP-10:
 *   Gate state changes only after successful POST response.
 *
 * ✓ C-31 / C-56 separation:
 *   Package generation causes a server refetch and never mutates gate state locally.
 *
 * ✓ INV-9:
 *   Closed/locked workflow explanation explicitly states package independence.
 *
 * ✓ Exceptions:
 *   Resolved blocking and warning exceptions remain visible with timestamps.
 *
 * ✓ XC-7 / DP-14:
 *   Gate status is explicit text; badge treatment only reinforces it.
 *
 * ✓ DP-8:
 *   Prohibited Finance approval language is absent.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - GET /api/v1/finance/period/{id}/close-status — Session 6 API gap.
 * - POST /api/v1/finance/period/{id}/close-status/advance — Session 6 API gap.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - Gate status is explicit text inside badges.
 * - Blocking and warning exception descriptions remain textual.
 * - Mutation failures use alert semantics.
 * - Native buttons provide keyboard interaction and disabled state.
 * - Close/locked/package distinctions do not rely on color.
 */

export default PeriodCloseWorkflow;
